import React, { useState } from 'react';
import { useTheme } from '../../styles/ThemeContext';

// Standard button: viewBox 243x74, fits ~18 chars
const STANDARD_VIEWBOX = "0 0 243 74";
const STANDARD_PATH = "M23.1473 58.5227L12.4152 40.3048C12.1434 39.8433 12 39.3176 12 38.782V15C12 13.3431 13.3431 12 15 12H215.268C216.33 12 217.313 12.5619 217.853 13.4773L228.585 31.6952C228.857 32.1567 229 32.6824 229 33.218V57C229 58.6569 227.657 60 226 60H25.7321C24.6697 60 23.6865 59.4381 23.1473 58.5227Z";
const STANDARD_FILTER_WIDTH = "243";

// Wide button: viewBox 450x74, same height but wider shape, fits ~35 chars
const WIDE_VIEWBOX = "0 0 450 74";
const WIDE_PATH = "M23.1473 58.5227L12.4152 40.3048C12.1434 39.8433 12 39.3176 12 38.782V15C12 13.3431 13.3431 12 15 12H422.268C423.33 12 424.313 12.5619 424.853 13.4773L435.585 31.6952C435.857 32.1567 436 32.6824 436 33.218V57C436 58.6569 434.657 60 433 60H25.7321C24.6697 60 23.6865 59.4381 23.1473 58.5227Z";
const WIDE_FILTER_WIDTH = "450";

const NeumorphicMainButton = ({
  text = "Apply Changes",
  fontColor,
  onClick = () => {},
  className = "",
  disabled = false,
  isActive = false,
  disableHover = false,
  wide = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const { theme, isDarkMode } = useTheme();

  const actualColor = fontColor || theme.primary;

  // Get current mode colors
  const currentColors = isDarkMode ? theme.colors.dark : theme.colors.light;

  // Get button background color based on state and mode
  const getButtonBackground = () => {
    if (disabled) return currentColors.surface;
    if (isActive || (!disableHover && isHovered)) return theme.primary;
    if (isPressed) return theme.secondary;
    return currentColors.background;
  };

  // Get shadow colors for SVG filters based on dark mode
  const shadowColors = isDarkMode ? {
    light: { r: 0.196, g: 0.196, b: 0.196 }, // rgba(50, 50, 50) — subtle lift, not white
    mid: { r: 0.039, g: 0.039, b: 0.039 },   // rgba(10, 10, 10)
    dark: { r: 0.078, g: 0.078, b: 0.078 }   // rgba(20, 20, 20)
  } : {
    light: { r: 1.0, g: 1.0, b: 1.0 },       // rgba(255, 255, 255)
    mid: { r: 0.812, g: 0.812, b: 0.812 },   // rgba(207, 207, 207)
    dark: { r: 0.812, g: 0.812, b: 0.812 }   // rgba(207, 207, 207)
  };

  // Select standard or wide SVG dimensions
  const viewBox = wide ? WIDE_VIEWBOX : STANDARD_VIEWBOX;
  const shapePath = wide ? WIDE_PATH : STANDARD_PATH;
  const filterWidth = wide ? WIDE_FILTER_WIDTH : STANDARD_FILTER_WIDTH;
  const filterId = wide ? 'filter_neumorphic_wide' : 'filter_neumorphic_standard';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => !disableHover && setIsHovered(true)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      className={`inline-block ${disabled ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!disableHover ? 'hover:scale-105' : ''} ${!disableHover ? 'active:scale-95' : ''} disabled:opacity-50 ${!disableHover ? 'disabled:cursor-not-allowed' : ''} w-full ${wide ? '' : 'max-w-xs sm:max-w-sm md:max-w-md'} ${className}`}
      style={{ background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <svg
        width="100%"
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="drop-shadow-sm min-w-0"
        preserveAspectRatio="xMidYMid meet"
        overflow="visible"
        style={{ pointerEvents: 'none' }}
      >
        <g filter={`url(#${filterId})`}>
          <path
            d={shapePath}
            fill={getButtonBackground()}
            style={{ transition: 'fill 0.2s ease' }}
          />
        </g>

        {/* Dynamic text */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={
            disabled ? actualColor :
            (isActive || isPressed || (!disableHover && isHovered)) ? "white" :
            actualColor
          }
          fontSize="20"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="500"
          style={{ transition: 'fill 0.2s ease' }}
        >
          {text}
        </text>

        <defs>
          <filter id={filterId} x="0" y="0" width={filterWidth} height="74" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="4" dy="4"/>
            <feGaussianBlur stdDeviation="5"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.9 0`}/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_517_1783"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-4" dy="-4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.light.r} 0 0 0 0 ${shadowColors.light.g} 0 0 0 0 ${shadowColors.light.b} 0 0 0 0.9 0`}/>
            <feBlend mode="normal" in2="effect1_dropShadow_517_1783" result="effect2_dropShadow_517_1783"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="4" dy="-4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.2 0`}/>
            <feBlend mode="normal" in2="effect2_dropShadow_517_1783" result="effect3_dropShadow_517_1783"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-4" dy="4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.2 0`}/>
            <feBlend mode="normal" in2="effect3_dropShadow_517_1783" result="effect4_dropShadow_517_1783"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow_517_1783" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.5 0`}/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow_517_1783"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.light.r} 0 0 0 0 ${shadowColors.light.g} 0 0 0 0 ${shadowColors.light.b} 0 0 0 0.3 0`}/>
            <feBlend mode="normal" in2="effect5_innerShadow_517_1783" result="effect6_innerShadow_517_1783"/>
          </filter>
        </defs>
      </svg>
    </button>
  );
};

export default NeumorphicMainButton;
